<template>
  <v-container
    class="ma-0 pa-0 green lighten-4"
    fluid
  >
    <map-component
      :map-id="mapId"
      style="height: 100%; position: relative"
      :draw-enabled="true"
      :marker-labels-permanent="markerLabelsPermanent"
    />
  </v-container>
</template>

<script>
import MapComponent from "../../map/Map";
import MapContainerMixins from "@/mixins/mapContainerMixins";
import nanoid from "nanoid";
import { format } from 'date-fns';

export default {
  data() {
    return {
      mapId: null,
      eventId: null,
      eventDataSources: [],
      devicesDataSources: []
    };
  },

  computed: {
    isAranAllEventsMap() {
      const showAll = this.$store.getters["events/showAllEvents"];
      const eventIds = this.$store.getters["events/eventIdsToShow"] || [];
      return showAll && eventIds.length > 1;
    },
    markerLabelsPermanent() {
      return this.isAranAllEventsMap;
    }
  },

  methods: {
    applyMapFeatureEventMeta(feature, item) {
      const ev = item && item.event;
      let eventId = ev;
      let eventTitle = "";
      if (ev && typeof ev === "object") {
        eventId = ev._id != null ? ev._id : ev;
        eventTitle = (ev.title || "").trim();
      }
      if (eventId != null) {
        feature.properties.eventId = eventId;
      }
      if (eventTitle) {
        feature.properties.eventTitle = eventTitle;
      } else if (eventId != null) {
        const stored = this.$store.state.entities.events[String(eventId)];
        if (stored && stored.title) {
          feature.properties.eventTitle = String(stored.title).trim();
        }
      }
    },
    generateDataSources() {
      // Check if we're in כולם mode (multiple events)
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      const isAllEventsMode = eventIdsToShow && eventIdsToShow.length > 1;

      console.log('Map generateDataSources - eventIdsToShow:', eventIdsToShow);
      console.log('Map generateDataSources - eventIdsToShow.length:', eventIdsToShow ? eventIdsToShow.length : 'undefined');
      console.log('Map generateDataSources - isAllEventsMode:', isAllEventsMode);

      if (isAllEventsMode) {
        // In כולם mode, create a single data source that aggregates all events
        console.log('Map generateDataSources - creating כולם mode data source with events:', eventIdsToShow);
        this.eventDataSources = [
          {
            name: "כל האירועים",
            parentId: "all-events",
            get: {
              url: process.env.API_BASE_URL + "/map-features",
              method: "get",
              params: {
                q: JSON.stringify({
                  event: { $in: eventIdsToShow }
                }),
                limit: 0
              },
              toGeoJson: data => {
                console.log('Map toGeoJson - data received:', data);
                console.log('Map toGeoJson - data.results.length:', data.results ? data.results.length : 'undefined');
                return {
                  type: "FeatureCollection",
                  features: data.results.map(item => {
                    let feature = item.feature;
                    feature.properties.id = item._id;
                    this.applyMapFeatureEventMeta(feature, item);
                    return feature;
                  })
                };
              }
            },
            create: {
              url: process.env.API_BASE_URL + "/map-features",
              method: "post",
              params: {},
              featureToObject: data => {
                delete data.meta;
                
                // Check if we're in כולם mode (multiple events)
                const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
                const isAllEventsMode = eventIdsToShow && eventIdsToShow.length > 1;
                
                if (isAllEventsMode) {
                  // In כולם mode, create for all events in the ARAN
                  console.log('Map create featureToObject - כולם mode, creating for all events:', eventIdsToShow);
                  
                  // Return data for all events - the Map component will handle the multiple API calls
                  return {
                    event: eventIdsToShow, // Pass all event IDs
                    feature: data,
                    _id: data.properties.id,
                    isAllEventsMode: true // Flag to indicate כולם mode
                  };
                } else {
                  // Single event mode - create for current event only
                  return {
                    event: this.eventId,
                    feature: data,
                    _id: data.properties.id
                  };
                }
              }
            },
            update: {
              url: process.env.API_BASE_URL + "/map-features",
              method: "patch",
              params: {},
              featureToObject: data => {
                delete data.meta;
                return {
                  event: data.properties.eventId || this.eventId,
                  feature: data,
                  _id: data.properties.id
                };
              }
            },
            remove: {
              url: process.env.API_BASE_URL + "/map-features",
              method: "delete"
            },
            defaultProperties: {
              markerType: "",
              iconColor: "white",
              color: "red"
            },
            editable: true,
            isActive: true
          }
        ];
      } else {
        // Generate data sources for single event
        this.eventDataSources = [
          {
            name: "אירוע נוכחי",
            parentId: this.eventId,
            get: {
              url: process.env.API_BASE_URL + "/map-features",
              method: "get",
              params: {
                q: JSON.stringify({
                  event: this.eventId
                }),
                limit: 0
              },
              toGeoJson: data => {
                return {
                  type: "FeatureCollection",
                  features: data.results.map(item => {
                    let feature = item.feature;
                    feature.properties.id = item._id;
                    this.applyMapFeatureEventMeta(feature, item);
                    return feature;
                  })
                };
              }
            },
            create: {
              url: process.env.API_BASE_URL + "/map-features",
              method: "post",
              params: {},
              featureToObject: data => {
                delete data.meta;
                return {
                  event: this.eventId,
                  feature: data,
                  _id: data.properties.id
                };
              }
            },
            update: {
              url: process.env.API_BASE_URL + "/map-features",
              method: "patch",
              params: {},
              featureToObject: data => {
                delete data.meta;
                return {
                  event: this.eventId,
                  feature: data,
                  _id: data.properties.id
                };
              }
            },
            remove: {
              url: process.env.API_BASE_URL + "/map-features",
              method: "delete"
            },
            defaultProperties: {
              markerType: "",
              iconColor: "white",
              color: "red"
            },
            editable: true,
            isActive: true
          }
        ];
      }

      // Generate devices data sources based on current eventId
      this.devicesDataSources = [
        {
          name: "אירוע נוכחי",
          parentId: this.eventId,
          get: {
            url: process.env.API_BASE_URL + "/map-features",
            method: "get",
            params: {
              q: JSON.stringify({
                event: this.eventId
              }),
              limit: 0
            },
            toGeoJson: data => {
              return {
                type: "FeatureCollection",
                features: data.results.map(item => {
                  let feature = item.feature;
                  feature.properties.id = item._id;
                  return feature;
                })
              };
            }
          },
          create: {
            url: process.env.API_BASE_URL + "/map-features",
            method: "post",
            params: {},
            featureToObject: data => {
              delete data.meta;
              return {
                event: this.eventId,
                feature: data,
                _id: data.properties.id
              };
            }
          },
          update: {
            url: process.env.API_BASE_URL + "/map-features",
            method: "patch",
            params: {},
            featureToObject: data => {
              delete data.meta;
              return {
                event: this.eventId,
                feature: data,
                _id: data.properties.id
              };
            }
          },
          remove: {
            url: process.env.API_BASE_URL + "/map-features",
            method: "delete"
          },
          defaultProperties: {
            markerType: "",
            iconColor: "white",
            color: "red"
          },
          editable: true,
          isActive: true
        }
      ];
    },
    setDataSources() {
      // Generate data sources first
      this.generateDataSources();
      
      console.log('Map setDataSources - eventDataSources:', this.eventDataSources);
      console.log('Map setDataSources - eventDataSources.length:', this.eventDataSources.length);
      
      this.$store.commit("maps/APPEND_DATA_SOURCES_GROUPS", {
        mapId: this.mapId,
        val: [
          {
            title: "שכבת אירוע",
            dataSources: this.eventDataSources
          }
        ]
      });
      this.generatePartnerTypesDataSources();
      this.generateEventsDataSources();
      this.generateActiveEventsDataSources();
      this.generateOtherDataSources();
    },
    refreshMapData() {
      console.log('Map - refreshing map data');
      // Force refresh of map data sources
      this.$store.commit("maps/REFRESH_DATA_SOURCES", {
        mapId: this.mapId,
        groupTitle: "שכבת אירוע"
      });
    },
    removeDataSource({ title }) {
      this.$store.commit("maps/REMOVE_DATA_SOURCES_GROUP", {
        mapId: this.mapId,
        title
      });
    },
    generateEventInreachDataSources({ eventId, isActive }) {
      let customersWithImei = this.eventCustomers.filter(item => {
        return !!item.imei;
      });

      let dataSources = [];
      let partnerTypes = Object.values(this.$store.state.entities.partnerTypes);
      if (Array.isArray(customersWithImei) && customersWithImei.length) {
        for (let customer of customersWithImei) {
          dataSources.push({
            name: `${customer.name}`,
            parentId: customer._id,
            get: {
              url: process.env.API_BASE_URL + "/sat/get-messages-by-imei",
              method: "get",
              params: {
                imei: customer.imei
              },
              toGeoJson: data => {
                return {
                  type: "FeatureCollection",
                  features: data.results.filter(item => {
                    let point = item.point;
                    return point.longitude && point.latitude
                  }).map(item => {
                    let point = item.point;
                    let feature = {
                      type: "Feature",
                      geometry: {
                        coordinates: [point.longitude, point.latitude],
                        type: 'Point'
                      },
                      properties: {
                        id: item._id
                      }
                    };

                    let time = format(item.timeStamp, 'DD/MM/YYYY HH:mm:ss');
                    feature.properties.title = time;
                    if (item.messageCode == 3) {
                      feature.properties.description = item.freeText;
                    }
                    return feature;
                  })
                };
              }
            },
            editable: false,
            overrideProperties: {
              markerType: "",
              glyph: "mdi-satellite-variant",
              iconColor: "white",
              color: "blue"
            },
            defaultProperties: {
              markerType: "",
              iconColor: "white",
              color: "blue"
            },
            isActive
          });
        }
      }
      this.$store.commit("maps/APPEND_DATA_SOURCES_GROUPS", {
        mapId: this.mapId,
        val: [
          {
            title: "מכשירים לוויניים",
            dataSources
          }
        ]
      });
    },
    fetchCustomers() {
      this.loading = true;
      this.removing = [];
      
      // Get event IDs to show from store
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      console.log('Map fetchCustomers - eventIdsToShow:', eventIdsToShow);
      
      this.$store
        .dispatch("API_GET", {
          url: "/customers",
          entityType: "customer",
          schema: "customerListSchema",
          params: {
            q: JSON.stringify({ event: { $in: eventIdsToShow } }),
            limit: 0
          },
          replace: true
        })
        .catch(err => {
          this.$store.dispatch("SHOW_SNACKBAR", {
            text: err.message,
            color: "error"
          });
        })
        .finally(() => {
          this.loading = false;
          this.setDataSources();
        });
    }
  },

  beforeMount() {
    this.mapId = nanoid();
  },

  beforeDestroy() {
    // Clean up global event listener
    this.$bus.$off('refetch-all-data');
  },

  mounted() {
    this.fetchCustomers();
    this.eventId = this.$route.params.id; // not as computed because of watch bug for eventCustomers
    
    // Listen for global refetch event
    this.$bus.$on('refetch-all-data', () => {
      console.log('Map - received global refetch event');
      this.refreshMapData();
    });
  },

  computed: {
    eventCustomers() {
      // Get event IDs to show from store
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      
      if (eventIdsToShow.length === 1) {
        // Single event mode
        return this.$store.getters["customers/eventCustomers"](this.eventId);
      } else {
        // Multiple events mode (כולם) - get customers from all events
        const allCustomers = [];
        eventIdsToShow.forEach(eventId => {
          const eventCustomers = this.$store.getters["customers/eventCustomers"](eventId);
          if (eventCustomers && eventCustomers.length > 0) {
            allCustomers.push(...eventCustomers);
          }
        });
        return allCustomers;
      }
    }
  },

  watch: {
    eventCustomers: {
      handler: function(val, oldVal) {
        this.removeDataSource({ title: "מכשירים לוויניים" });
        this.generateEventInreachDataSources({
          eventId: this.eventId,
          isActive: false
        });
        this.generateInreachLastPositionsDataSources();
      },
      immediate: true
    },
    // Watch for route changes to refetch data when switching events
    '$route.params.id': {
      handler(newEventId) {
        if (newEventId && newEventId !== this.eventId) {
          this.eventId = newEventId;
          this.fetchCustomers();
        }
      },
      immediate: true
    },
    // Watch for eventId changes to regenerate data sources
    eventId: {
      handler(newEventId) {
        if (newEventId) {
          console.log('Map - eventId changed to:', newEventId);
          // Remove existing data sources and regenerate
          this.removeDataSource({ title: "שכבת אירוע" });
          this.setDataSources();
        }
      },
      immediate: true
    },
    // Watch for eventIdsToShow changes (כולם mode)
    '$store.state.events.eventIdsToShow': {
      handler(newEventIds, oldEventIds) {
        console.log('Map - eventIdsToShow changed from:', oldEventIds, 'to:', newEventIds);
        // Remove existing data sources and regenerate
        this.removeDataSource({ title: "שכבת אירוע" });
        this.generateDataSources(); // Regenerate data sources
        this.setDataSources(); // Set the new data sources
        this.fetchCustomers(); // Refetch customers for all events
      },
      deep: true
    },
    // Watch for showAllEvents changes (כולם toggle)
    '$store.state.events.showAllEvents': {
      handler(newShowAll, oldShowAll) {
        console.log('Map - showAllEvents changed from:', oldShowAll, 'to:', newShowAll);
        // Remove existing data sources and regenerate
        this.removeDataSource({ title: "שכבת אירוע" });
        this.generateDataSources(); // Regenerate data sources
        this.setDataSources(); // Set the new data sources
        this.fetchCustomers(); // Refetch customers for all events
      }
    }
  },

  components: {
    MapComponent
  },

  mixins: [MapContainerMixins]
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/events/modules/Map.vue